/**
 * What reaches *you*, and where — `design-remediation.md` WP2, card 3.
 *
 * Not `notifications.ts`, which is the customer's side: what a property's
 * contacts are told about a visit, an invoice or a payment, on the channels
 * that customer agreed to. This is the other side of the same word — the five
 * things that happen inside the company and the four ways one of them can
 * reach a person who works here. Two different audiences, two different lists,
 * and mixing them would mean one switch that quietly changed both.
 *
 * The rule lives here rather than in the page or the (not yet written) sender,
 * so there is one answer to "does this reach Rita by SMS at 11pm" and the
 * screen that sets it and the code that obeys it cannot disagree.
 */
import { z } from 'zod';
/** The five things worth telling somebody who works here about. */
export const MY_NOTIFICATION_KINDS = [
    'approvals', 'exceptions', 'mentions', 'schedule', 'money',
];
export const MY_NOTIFICATION_LABELS = {
    approvals: 'Approvals',
    exceptions: 'Exceptions',
    mentions: 'Mentions',
    schedule: 'Schedule changes',
    money: 'Money',
};
/** The line under each, from the mockup — what the word actually covers. */
export const MY_NOTIFICATION_BLURBS = {
    approvals: 'quotes approved, POs to approve',
    exceptions: 'over capacity, missed clock-ins, failed sends',
    mentions: '@you on any record',
    schedule: "your crews' visits moved",
    money: 'payments received, invoices overdue',
};
export const MY_NOTIFICATION_CHANNELS = ['inApp', 'push', 'email', 'sms'];
export const MY_NOTIFICATION_CHANNEL_LABELS = {
    inApp: 'In-app', push: 'Push', email: 'Email', sms: 'SMS',
};
/**
 * Quiet hours as two local clock times, `HH:MM`.
 *
 * `from` after `to` is the ordinary case — 21:00 to 06:00 crosses midnight —
 * so `inQuietHours` has to wrap rather than compare a single range.
 */
export const quietHoursSchema = z.object({
    from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
/**
 * The four switches for one kind, each optional.
 *
 * Spelled out rather than built from `MY_NOTIFICATION_CHANNELS`, because a
 * `z.record` keyed by an enum types every key as present and this object is
 * mostly absent keys — an absent one means "whatever the default is", which is
 * what lets a new channel ship without a migration. `my-notifications.test.ts`
 * checks these keys against the arrays above so the two cannot drift.
 */
const channelFlagsSchema = z.object({
    inApp: z.boolean().optional(),
    push: z.boolean().optional(),
    email: z.boolean().optional(),
    sms: z.boolean().optional(),
});
const matrixSchema = z.object({
    approvals: channelFlagsSchema.optional(),
    exceptions: channelFlagsSchema.optional(),
    mentions: channelFlagsSchema.optional(),
    schedule: channelFlagsSchema.optional(),
    money: channelFlagsSchema.optional(),
});
export const myNotificationPrefsSchema = z.object({
    /** Missing kind, or missing channel inside one, means the default below. */
    matrix: matrixSchema.default({}),
    quietHours: quietHoursSchema.nullable().default(null),
});
/**
 * What somebody gets before they have said anything.
 *
 * In-app on for everything, because it costs nobody anything and the
 * alternative is a person who never learns the bell means something. Email on
 * for the three that need an answer, off for the two that are a running
 * commentary. Push and SMS off: those interrupt, and interrupting is opt-in.
 */
export const MY_NOTIFICATION_DEFAULTS = {
    approvals: { inApp: true, push: false, email: true, sms: false },
    exceptions: { inApp: true, push: false, email: true, sms: false },
    mentions: { inApp: true, push: false, email: true, sms: false },
    schedule: { inApp: true, push: false, email: false, sms: false },
    money: { inApp: true, push: false, email: false, sms: false },
};
/** The whole matrix, defaults filled in — what a screen draws. */
export function resolveMatrix(prefs) {
    const out = {};
    for (const kind of MY_NOTIFICATION_KINDS) {
        const saved = prefs?.matrix?.[kind];
        out[kind] = { ...MY_NOTIFICATION_DEFAULTS[kind] };
        for (const channel of MY_NOTIFICATION_CHANNELS) {
            const value = saved?.[channel];
            if (typeof value === 'boolean')
                out[kind][channel] = value;
        }
    }
    return out;
}
/**
 * Whether this kind may go out on this channel, right now.
 *
 * `at` is the moment in the person's own time zone, as `HH:MM` — the caller
 * knows where they are and this does not. Quiet hours hold back push and SMS
 * only: an email or an in-app note waits in a list until it is read, which is
 * the whole difference between a message and an interruption.
 */
export function allowsChannel(prefs, kind, channel, at) {
    if (!resolveMatrix(prefs)[kind][channel])
        return false;
    if (channel !== 'push' && channel !== 'sms')
        return true;
    return !inQuietHours(prefs?.quietHours ?? null, at);
}
/** Whether `at` (`HH:MM`, local) falls inside the quiet window. */
export function inQuietHours(quiet, at) {
    if (!quiet || !at)
        return false;
    const minutes = (clock) => {
        const [hours, mins] = clock.split(':');
        return Number(hours) * 60 + Number(mins);
    };
    const now = minutes(at);
    const from = minutes(quiet.from);
    const to = minutes(quiet.to);
    // 21:00 → 06:00 wraps midnight; 13:00 → 14:00 does not. Equal means never.
    if (from === to)
        return false;
    return from < to ? now >= from && now < to : now >= from || now < to;
}
/** "9 pm – 6 am", for the line under the matrix. */
export function quietHoursLabel(quiet) {
    if (!quiet)
        return 'off';
    const say = (clock) => {
        const [hours, mins] = clock.split(':').map(Number);
        const suffix = hours < 12 ? 'am' : 'pm';
        const hour = hours % 12 === 0 ? 12 : hours % 12;
        return mins === 0 ? `${hour} ${suffix}` : `${hour}:${String(mins).padStart(2, '0')} ${suffix}`;
    };
    return `${say(quiet.from)} – ${say(quiet.to)}`;
}
//# sourceMappingURL=my-notifications.js.map